/**
 * AmbientePanel — drill-down de 1 ambiente do pedido (aberto ao clicar num chip
 * de ambiente no cockpit do cliente). Fiel ao mockup fase-3 "Detalhe Cliente".
 *
 * Mostra dados REAIS:
 *  - resumo: valor (soma dos itens) + m² de chapas + material principal;
 *  - status de produção: stepper horizontal das estações do roteiro, com a
 *    estação corrente = gargalo das peças (OrdemProducao/bipagem) + "X de N
 *    peças concluídas";
 *  - componentes & ferragens: BOM CAD (ferragens rateadas + fita de borda)
 *    vindo do DinaBox (ADR-0019);
 *  - "Ver OP de produção" → lista de OPs do pedido.
 *
 * O render do ambiente é placeholder (o DinaBox não expõe/armazenamos imagem
 * hoje) — espelha o mockup ("RENDER · <NOME>.JPG").
 */
import React from "react";
import { FiCheck, FiExternalLink, FiPackage } from "react-icons/fi";

import SlideOverPanel from "../../components/SlideOverPanel";

function fmtBRLCheio(valor) {
  const n = Number(valor) || 0;
  return `R$ ${Math.round(n).toLocaleString("pt-BR")}`;
}

function fmtQtd(n) {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toLocaleString("pt-BR");
}

function EstacoesStepper({ estacoes = [] }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start" }}>
      {estacoes.map((e, i) => {
        const done = e.estado === "done";
        const current = e.estado === "current";
        const bg = done ? "var(--success-solid)" : current ? "var(--accent)" : "var(--surface-2)";
        const fg = done || current ? "#fff" : "var(--ink-3)";
        return (
          <div
            key={e.codigo}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              minWidth: 0,
            }}
          >
            {i < estacoes.length - 1 && (
              <div
                style={{
                  position: "absolute",
                  top: 13,
                  left: "50%",
                  right: "-50%",
                  height: 2,
                  background: done ? "var(--success-solid)" : "var(--border)",
                  zIndex: 0,
                }}
              />
            )}
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 999,
                background: bg,
                color: fg,
                display: "grid",
                placeItems: "center",
                fontSize: 12,
                fontWeight: 600,
                zIndex: 1,
                boxShadow: current ? "0 0 0 3px var(--accent-soft)" : "none",
              }}
            >
              {done ? <FiCheck size={14} aria-hidden="true" /> : i + 1}
            </div>
            <span
              style={{
                fontSize: 10,
                marginTop: 6,
                color: current ? "var(--ink)" : "var(--ink-3)",
                fontWeight: current ? 600 : 400,
                textAlign: "center",
                lineHeight: 1.2,
              }}
            >
              {e.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Card({ label, children }) {
  return (
    <div
      className="cmp"
      style={{ flex: 1, padding: "12px 14px", minWidth: 0 }}
    >
      <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-3)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 600,
          fontSize: 20,
          letterSpacing: "-0.02em",
          marginTop: 2,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function AmbientePanel({ open, loading, data, onClose, onVerOps }) {
  const amb = data?.ambiente;
  const resumo = data?.resumo;
  const producao = data?.producao;
  const componentes = data?.componentes || [];

  const subtitle =
    amb && resumo
      ? `${amb.pedido_numero} · ${resumo.m2_chapas} m²${
          resumo.material_principal ? ` · ${resumo.material_principal}` : ""
        }`
      : amb?.pedido_numero || "";

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={amb?.nome || "Ambiente"}
      subtitle={subtitle}
      width="min(96vw, 560px)"
    >
      {loading ? (
        <p style={{ color: "var(--ink-3)", fontSize: 14, padding: "8px 0" }}>
          Carregando ambiente…
        </p>
      ) : !data ? null : (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* Render placeholder (DinaBox não fornece imagem hoje) */}
          <div
            style={{
              borderRadius: "var(--radius-md)",
              background: "var(--surface-2)",
              border: "1px dashed var(--border)",
              minHeight: 150,
              display: "grid",
              placeItems: "center",
              color: "var(--ink-4, var(--ink-3))",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.04em",
              textAlign: "center",
              padding: 12,
            }}
          >
            RENDER · {(amb?.nome || "AMBIENTE").toUpperCase()}.JPG (placeholder)
          </div>

          {/* Valor + m² */}
          <div style={{ display: "flex", gap: 10 }}>
            <Card label="Valor">{fmtBRLCheio(resumo?.valor_total)}</Card>
            <Card label="m² chapas">
              {resumo?.m2_chapas ?? 0} <span style={{ fontSize: 13, color: "var(--ink-3)" }}>m²</span>
            </Card>
          </div>

          {/* Status produção */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              Status produção
            </div>
            {producao?.tem_ops ? (
              <>
                <EstacoesStepper estacoes={producao.estacoes} />
                <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 14, textAlign: "center" }}>
                  {producao.pecas_concluidas} de {producao.pecas_total}{" "}
                  {producao.pecas_total === 1 ? "peça concluída" : "peças concluídas"}
                  {producao.situacao_resumo ? ` · ${producao.situacao_resumo}` : ""}
                </div>
              </>
            ) : (
              <div
                className="so-banner"
                style={{
                  fontSize: 13,
                  color: "var(--ink-3)",
                  background: "var(--surface-2)",
                  borderRadius: "var(--radius-md)",
                  padding: "10px 12px",
                }}
              >
                Produção não iniciada — sem ordens de produção geradas para este ambiente.
              </div>
            )}
          </div>

          {/* Componentes & ferragens */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Componentes &amp; ferragens
            </div>
            {componentes.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--ink-3)", display: "flex", gap: 8, alignItems: "center" }}>
                <FiPackage size={14} aria-hidden="true" />
                Sem detalhamento CAD (DinaBox) para este ambiente.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {componentes.map((c, i) => (
                  <div
                    key={i}
                    className="flex between"
                    style={{
                      padding: "9px 2px",
                      borderTop: i === 0 ? "0" : "1px solid var(--border)",
                      fontSize: 13,
                    }}
                  >
                    <span>{c.nome}</span>
                    <span className="mono" style={{ color: "var(--ink-2)" }}>
                      {fmtQtd(c.quantidade)} {c.unidade}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Painel de Produção (v2) — a lista de OPs avulsa foi removida */}
          {producao?.tem_ops && (
            <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 4 }}>
              <button type="button" className="btn btn--secondary" onClick={onVerOps}>
                <FiExternalLink size={14} aria-hidden="true" /> Ver produção
              </button>
            </div>
          )}
        </div>
      )}
    </SlideOverPanel>
  );
}
